import React, { useEffect, useRef, useState } from "react";
import BookingForm from "./ExistingUserForm";
import GuestForm from "./GuestForm";
import { fetchExistingUsers } from "../../services/adminApiService/venueManagerApi/venueManagerApi.js";
import "./styles.css";

const compactName = (...parts) => parts.filter(Boolean).join(" ").trim();

const getUserName = (user) =>
  user?.username ||
  user?.name ||
  compactName(user?.first_name, user?.middle_name, user?.last_name) ||
  compactName(
    user?.user?.first_name,
    user?.user?.middle_name,
    user?.user?.last_name,
  ) ||
  user?.user?.name ||
  "Unnamed User";

const getUserType = (user) =>
  user?.user_type ||
  user?.user_type_name ||
  user?.user?.user_type ||
  user?.user?.user_type_name ||
  "";

const getUserAtomId = (user) =>
  user?.atom_id ||
  user?.user?.atom_id ||
  user?.individual?.atom_id ||
  user?.indivisual?.atom_id ||
  "";

const getUserEmail = (user) =>
  user?.email ||
  user?.email_id ||
  user?.user_email ||
  user?.user?.email ||
  user?.individual?.email ||
  user?.indivisual?.email ||
  "";

const getUserMobile = (user) =>
  user?.mobile ||
  user?.phone ||
  user?.contact_no ||
  user?.contact_number ||
  user?.user?.mobile ||
  user?.user?.phone ||
  user?.individual?.mobile ||
  user?.indivisual?.mobile ||
  "";

const getUserProfileImage = (user) =>
  user?.profile_image ||
  user?.user?.profile_image ||
  user?.indivisul_doc?.doc_file ||
  "https://i.pravatar.cc/40?img=1";

const normalizeUsersResponse = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  const nestedList = [
    payload.data,
    payload.users,
    payload.results,
    payload.individuals,
    payload.records,
    payload.rows,
  ].find(Array.isArray);

  if (nestedList) return nestedList;

  return payload.id || payload.user_id || payload.atom_id || payload.user
    ? [payload]
    : [];
};

// const usersData = [
//   {
//     id: "#909000",
//     name: "Aryan Kumar",
//     role: "Athlete",
//     mobile: "+91 123 4567 890",
//     image: "https://i.pravatar.cc/40?img=1",
//   },
//   {
//     id: "#909001",
//     name: "Aryan Kumar",
//     role: "Coach",
//     mobile: "+91 987 6543 210",
//     image: "https://i.pravatar.cc/40?img=2",
//   },
// ];

export default function UserToggle() {
  const [activeTab, setActiveTab] = useState("existing");
  const [search, setSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [usersData, setUsersData] = useState([]);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const latestSearchRef = useRef("");
  const filteredUsers = Array.isArray(usersData) ? usersData : [];

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500); // ⏱ delay

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!debouncedSearch.trim()) {
      setUsersData([]);
      setLoading(false);
      return;
    }

    let isCurrentRequest = true;

    const getExistingUsers = async () => {
      try {
        setLoading(true);

        const res = await fetchExistingUsers(debouncedSearch);
        if (
          isCurrentRequest &&
          latestSearchRef.current.trim() === debouncedSearch.trim()
        ) {
          setUsersData(normalizeUsersResponse(res?.data?.data ?? res?.data));
        }
      } catch (err) {
        console.error("Error fetching existing users:", err);
      } finally {
        if (
          isCurrentRequest &&
          latestSearchRef.current.trim() === debouncedSearch.trim()
        ) {
          setLoading(false);
        }
      }
    };

    getExistingUsers();

    return () => {
      isCurrentRequest = false;
    };
  }, [debouncedSearch]);

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    latestSearchRef.current = "";
    setSearch("");
    setShowDropdown(false);
    setLoading(false);
    localStorage.setItem("selectedExistingUser", JSON.stringify(user));
  };

  return (
    <div className="userToggle-wrapper">
      {/* Toggle */}
      <div className="userToggle-toggle">
        <button
          className={`userToggle-tab ${
            activeTab === "existing" ? "active" : ""
          }`}
          onClick={() => setActiveTab("existing")}
        >
          Existing User
        </button>

        <button
          className={`userToggle-tab ${activeTab === "guest" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("guest");
            setSelectedUser(null);
          }}
        >
          Guest
        </button>
      </div>

      {/* Existing User */}
      {activeTab === "existing" && (
        <div className="userToggle-card">
          <h3 className="userToggle-title">For Existing Users</h3>

          {/* Selected User Card */}
          {selectedUser ? (
            <div className="userToggle-selectedBox">
              <div className="userToggle-userRow">
                <img
                  src={getUserProfileImage(selectedUser)}
                  alt=""
                  className="userToggle-avatar"
                />

                <div className="userToggle-info">
                  <div style={{display: "flex", alignItems: "center", gap: "12px"}}>
                    <div>
                      <div className="userToggle-name">
                        {getUserName(selectedUser)}
                      </div>
                      <div className="userToggle-role">
                        {getUserType(selectedUser)}
                      </div>
                    </div>

                    <div>
                      {" "}
                      <span className="userToggle-id">
                        {getUserAtomId(selectedUser)}
                      </span>
                    </div>
                  </div>
                </div>

                <button
                  className="userToggle-remove"
                  onClick={() => setSelectedUser(null)}
                >
                  ✕ Remove
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Input */}
              <input
                className="userToggle-input"
                placeholder="Enter Atom ID / Email / Mobile"
                value={search}
                onChange={(e) => {
                  const nextSearch = e.target.value;
                  latestSearchRef.current = nextSearch;
                  setSearch(nextSearch);
                  setUsersData([]);
                  setLoading(Boolean(nextSearch.trim()));
                  setShowDropdown(Boolean(nextSearch));
                }}
              />

              {/* Dropdown */}
              {showDropdown && search && (
                <div className="userToggle-dropdown">
                  {loading ? (
                    <div className="userToggle-empty">Loading...</div>
                  ) : filteredUsers.length > 0 ? (
                    filteredUsers.map((user, index) => (
                      <div
                        key={index}
                        className="userToggle-userRow"
                        onClick={() => handleSelectUser(user)}
                      >
                        <img
                          src={getUserProfileImage(user)}
                          alt=""
                          className="userToggle-avatar"
                        />

                        <div className="userToggle-info">
                          <div className="userToggle-name">
                            {getUserName(user)}
                          </div>
                          <div className="userToggle-role">
                            {getUserType(user) ||
                              getUserEmail(user) ||
                              getUserMobile(user)}
                          </div>
                        </div>

                        <span className="userToggle-id">
                          {getUserAtomId(user)}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="userToggle-empty">No users found</div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {selectedUser && <BookingForm selectedUser={selectedUser} />}
      {activeTab === "guest" && <GuestForm />}
    </div>
  );
}
